/**
 *  Widgets - 3Way
 *  @module Widgets
 *  @author Livescore <info@score-stats.com>
 *  @copyright 2021 livescore
 */

import { MatchService } from '@services/match.service';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { BaseComponent } from '@widgets/pages/base.component';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { DemoService } from '@widgets/services/demo.service';
import { WidgetMixin } from '@widgets/services/mixins/widget.mixin';

import { Form, LastForm } from '@widgets/interfaces/widget.interface';
import CONFIG from '@widgets/config/widget.config';

import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';

import { MatchType } from '@interfaces/league-table.interface';
import { ClientService } from '@services/client.service';
import { MatchData, Team } from '@interfaces/match-data.interface';
import { LangService } from '@services/lang.service';
import { OddsAndMarkets } from '@interfaces/bets-market.interface';


@Component({
    selector: 'widgets-3way',
    templateUrl: './3way.component.html',
    styleUrls: ['../styles.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ThreeWayComponent extends WidgetMixin(BaseComponent) implements OnInit, OnDestroy {
    public matches: MatchData[] = [];

    private matchId: number;

    private subscriber: Subscription;

    public data: Form | null = null;
    public dataWin: Form | null = null;

    public odds: number[] | null = null;

    public num: number;

    public otherOdds: OddsAndMarkets;

    public constructor(
        private router: ActivatedRoute,
        private match: MatchService,
        private lang: LangService,
        private demo: DemoService,
        public client: ClientService,
        private trans: TranslateService,
    ) {
        super(demo);
    }

    /**
     * Load data from server
     */
    public ngOnInit(): void {
        super.ngOnInit();

        this.setTitle(this.router);

        this.router.paramMap.subscribe((params: ParamMap) => {
            if (params.has('odds')) {
                this.odds = params.get('odds')!.split(',').map(v => +v);
            }
            if (params.has('oddsData')) {
                this.otherOdds = (JSON.parse(decodeURI(`${params.get('oddsData')}`)));
            }
        });

        this.router.data.subscribe((sub) => {
            this.num = sub.num ?? 20;

            if (sub.br && sub.br.length > 0) {
                [this.matchId] = sub.br;
            } else {
                this.finished();
            }

            if (this.matchId) {
                this.load();
            } else {
                this.error();
            }
        });
    }

    public ngOnDestroy(): void {
        if (this.subscriber instanceof Subscription) {
            this.subscriber.unsubscribe();
        }

        this.matches.splice(0);
    }

    get drawTrans(): string {
        return this.trans.instant('web.draw_match');
    }

    private load(): void {
        const iso: string | null = this.lang.getLangSnapshot();
        let homeId: number | null = null;
        let awayId: number | null = null;

        this.subscriber = this.match
            .getMatchNew(this.matchId, iso as string, true)
            .pipe(
                filter((match: MatchData) => CONFIG.form.availSports.includes(match.sport_code_name)),
                mergeMap(
                    (match: MatchData): Observable<Form> => {
                        const [p1, p2] = match.participants.map((p: Team): number => p.id);
                        homeId = p1;
                        awayId = p2;
                        return this.demo.form(p1, p2, iso as string, { limit: this.num });
                    },
                ),
                map((data: Form) => {
                    data.home = { ...data.home, id: homeId };
                    data.away = { ...data.away, id: awayId };

                    data.home.form = data.home.form.filter((f: LastForm) => f.type !== MatchType.UPCOMING);
                    data.away.form = data.away.form.filter((f: LastForm) => f.type !== MatchType.UPCOMING);
                    return data;
                }),
                switchMap(this.checkEmpty()),
                catchError(
                    (e): Observable<any> => {
                        this.error(e);
                        return throwError(e);
                    },
                ),
            )

            .subscribe(
                (data) => {
                    this.data = data;
                    this.dataWin = cloneDeep(data);
                    this.data!.home.form = data.home.form.reverse().slice(0, 10);
                    this.data!.away.form = data.away.form.reverse().slice(0, 10);

                    this.finished();
                },
                () => {},
                () => {
                    this.loaded = true;
                },
            );
    }
}
